import { ATTACHMENT_NODE_TYPE, attachmentAsText, type AttachmentNote } from "./attachments";
import type { SerializedNodeLike } from "./types";

export const TABLE_NODE_TYPE = "table";
export const TABLE_ROW_NODE_TYPE = "tablerow";
export const TABLE_CELL_NODE_TYPE = "tablecell";

/** `headerState` is two bits: the cell is in a header row (1), in a header column (2), or both. */
export const HEADER_ROW = 1;
export const HEADER_COLUMN = 2;

/**
 * What a paste had that a table here cannot hold, and what became of it. Text is never dropped:
 * a merged cell keeps its content in its first cell, and anything that was not a paragraph is
 * kept as paragraphs.
 */
export type TableNote =
  /** Cells spanning rows or columns were split into single cells. */
  | "merged-cells-split"
  /** A table inside a cell became that cell's text. */
  | "nested-table-flattened"
  /** Lists, headings, quotes, or code inside a cell became paragraphs. */
  | "cell-blocks-flattened"
  /** This editor has no tables, so the table became lines of text, a row each. */
  | "table-flattened";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const childrenOf = (node: unknown): unknown[] =>
  isRecord(node) && Array.isArray(node.children) ? node.children : [];

const span = (value: unknown): number =>
  typeof value === "number" && Number.isInteger(value) && value > 1 ? value : 1;

export interface TableSize {
  rows: number;
  columns: number;
}

/**
 * The grid a table occupies once every span is counted, or `null` as soon as it passes either
 * maximum. Runs on unchecked JSON, so a hostile `colSpan` costs nothing: the walk stops at the
 * limit instead of laying the cells out.
 */
export function measureTable(
  table: unknown,
  maxRows: number,
  maxColumns: number,
): TableSize | null {
  const rows = childrenOf(table);
  if (rows.length > maxRows) return null;
  // Columns still covered from above, as the number of further rows each reaches into.
  let carried: number[] = [];
  let columns = 0;
  let height = rows.length;
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    const next: number[] = [];
    let column = 0;
    const skipCovered = (): void => {
      while ((carried[column] ?? 0) > 0) next[column] = carried[column++]! - 1;
    };
    for (const cell of childrenOf(rows[rowIndex])) {
      skipCovered();
      const width = span(isRecord(cell) ? cell.colSpan : 1);
      const reach = span(isRecord(cell) ? cell.rowSpan : 1);
      if (column + width > maxColumns || rowIndex + reach > maxRows) return null;
      for (let offset = 0; offset < width; offset++) next[column++] = reach - 1;
      height = Math.max(height, rowIndex + reach);
    }
    // Cells reaching down past the end of this row's own cells.
    while (column < carried.length) {
      next[column] = Math.max((carried[column] ?? 0) - 1, 0);
      column++;
    }
    columns = Math.max(columns, next.length);
    carried = next;
  }
  return { rows: height, columns };
}

/** Whether content holds a table anywhere. Iterative: it runs on documents of any size. */
export function usesTables(root: unknown): boolean {
  const stack: unknown[] = [root];
  while (stack.length > 0) {
    const node = stack.pop();
    if (!isRecord(node)) continue;
    if (node.type === TABLE_NODE_TYPE) return true;
    if (Array.isArray(node.children)) for (const child of node.children) stack.push(child);
  }
  return false;
}

const paragraph = (children: SerializedNodeLike[] = []): SerializedNodeLike => ({
  type: "paragraph",
  version: 1,
  children,
});

const emptyCell = (headerState: number): SerializedNodeLike => ({
  type: TABLE_CELL_NODE_TYPE,
  version: 1,
  headerState,
  colSpan: 1,
  rowSpan: 1,
  backgroundColor: null,
  children: [paragraph()],
});

/** Highlighted code is text once it leaves its code block. */
const asInline = (node: SerializedNodeLike): SerializedNodeLike =>
  node.type === "code-highlight"
    ? { type: "text", version: 1, text: String(node.text ?? "") }
    : node;

/**
 * The paragraphs a block amounts to where only paragraphs can stand: a line for each item of a
 * list, a row for each row of a table, and the inline content of anything else. The depth of the
 * input was bounded by the walk that checked it.
 */
export function flattenToParagraphs(
  node: SerializedNodeLike,
  notes: Set<TableNote | AttachmentNote>,
): SerializedNodeLike[] {
  // A block with no text of its own, which a cell cannot hold: what is left of it is its name.
  if (node.type === ATTACHMENT_NODE_TYPE) {
    notes.add("attachment-flattened");
    const label = attachmentAsText(node);
    return label === null ? [] : [paragraph([label])];
  }
  // Text with no block around it gets one, rather than being lost with the block it is not in.
  if (node.children === undefined && node.type !== "horizontalrule") {
    return node.type === "linebreak" ? [] : [paragraph([asInline(node)])];
  }
  const children = node.children ?? [];
  switch (node.type) {
    case "paragraph":
      return [node];
    case "horizontalrule":
      notes.add("cell-blocks-flattened");
      return [];
    case TABLE_NODE_TYPE:
      notes.add("nested-table-flattened");
      return children.flatMap((row) => rowToParagraph(row, notes));
    case "list":
    case "listitem": {
      notes.add("cell-blocks-flattened");
      // An item holds either its own line or the nested list that hangs off the item before it.
      const nested = children.some((child) => child.type === "list");
      return nested || node.type === "list"
        ? children.flatMap((child) => flattenToParagraphs(child, notes))
        : [paragraph(children.map(asInline))];
    }
    default:
      notes.add("cell-blocks-flattened");
      return children.some(
        (child) =>
          child.children !== undefined && child.type !== "link" && child.type !== "autolink",
      )
        ? children.flatMap((child) => flattenToParagraphs(child, notes))
        : [paragraph(children.map(asInline))];
  }
}

const TAB: SerializedNodeLike = {
  type: "tab",
  version: 1,
  text: "\t",
  format: 0,
  mode: "normal",
  style: "",
};
const SPACE: SerializedNodeLike = { type: "text", version: 1, text: " " };

/** One line for a row: its cells a tab apart, and the paragraphs of a cell a space apart. */
function rowToParagraph(
  row: SerializedNodeLike,
  notes: Set<TableNote | AttachmentNote>,
): SerializedNodeLike[] {
  const inline: SerializedNodeLike[] = [];
  (row.children ?? []).forEach((cell, cellIndex) => {
    if (cellIndex > 0) inline.push({ ...TAB });
    const lines = (cell.children ?? []).flatMap((child) => flattenToParagraphs(child, notes));
    lines.forEach((line, lineIndex) => {
      if (lineIndex > 0 && (line.children ?? []).length > 0) inline.push({ ...SPACE });
      inline.push(...(line.children ?? []));
    });
  });
  return [paragraph(inline)];
}

/** A whole table as lines of text, for an editor that has no tables. */
export function flattenTable(
  table: SerializedNodeLike,
  notes: Set<TableNote | AttachmentNote>,
): SerializedNodeLike[] {
  notes.add("table-flattened");
  const inner = new Set<TableNote | AttachmentNote>();
  return (table.children ?? []).flatMap((row) => rowToParagraph(row, inner));
}

/**
 * A pasted table as one this editor can hold: every cell single, every row the same length, and
 * nothing but paragraphs inside a cell. A merged cell keeps its content where it started, and the
 * places it covered become empty cells with the same header state, as upstream's own unmerge has
 * it. The caller has measured the table, so the grid built here is within the limits.
 */
export function normalizeTable(
  table: SerializedNodeLike,
  notes: Set<TableNote | AttachmentNote>,
): SerializedNodeLike {
  const rows = table.children ?? [];
  const grid: (SerializedNodeLike | undefined)[][] = rows.map(() => []);
  rows.forEach((row, rowIndex) => {
    let column = 0;
    for (const cell of row.children ?? []) {
      while (grid[rowIndex]![column] !== undefined) column++;
      const width = span(cell.colSpan);
      const reach = span(cell.rowSpan);
      if (width > 1 || reach > 1) notes.add("merged-cells-split");
      const headerState = typeof cell.headerState === "number" ? cell.headerState : 0;
      for (let down = 0; down < reach; down++) {
        // A span reaching below the last row makes rows of its own.
        const line = (grid[rowIndex + down] ??= []);
        for (let across = 0; across < width; across++) {
          line[column + across] =
            down === 0 && across === 0
              ? {
                  ...cell,
                  headerState,
                  colSpan: 1,
                  rowSpan: 1,
                  backgroundColor: null,
                  children: cellParagraphs(cell, notes),
                }
              : emptyCell(headerState);
        }
      }
      column += width;
    }
  });
  const columns = Math.max(1, ...grid.map((line) => line.length));
  const children = grid.map((line, rowIndex): SerializedNodeLike => {
    // A padding cell heads its row when the row is a header row, and never its column.
    const first = line.find((cell) => cell !== undefined);
    const headerState = typeof first?.headerState === "number" ? first.headerState & HEADER_ROW : 0;
    const cells = Array.from(
      { length: columns },
      (_, column) => line[column] ?? emptyCell(headerState),
    );
    const source = rows[rowIndex];
    return source === undefined
      ? { type: TABLE_ROW_NODE_TYPE, version: 1, children: cells }
      : { ...source, children: cells };
  });
  // Widths were given per original column and say nothing about a grid that changed shape.
  const { colWidths, ...rest } = table;
  const keepWidths = Array.isArray(colWidths) && colWidths.length === columns;
  return { ...rest, ...(keepWidths ? { colWidths } : {}), children };
}

function cellParagraphs(
  cell: SerializedNodeLike,
  notes: Set<TableNote | AttachmentNote>,
): SerializedNodeLike[] {
  const lines = (cell.children ?? []).flatMap((child) => flattenToParagraphs(child, notes));
  return lines.length > 0 ? lines : [paragraph()];
}

// The edits are named here, not beside the commands that make them: the intent union names
// them, and the file it is in reaches no Lexical, which the commands' file does.

/** What can be done to the table the selection is in. */
export type TableEdit =
  | { type: "insert-row"; where: "above" | "below" }
  /** `before` is the side text starts from: left in a left-to-right document. */
  | { type: "insert-column"; where: "before" | "after" }
  /** Every row the selection touches. Deleting the last rows deletes the table. */
  | { type: "delete-row" }
  | { type: "delete-column" }
  /** Whether the first row is a header row. */
  | { type: "set-header-row"; header: boolean }
  /** Whether the first column is a header column. */
  | { type: "set-header-column"; header: boolean }
  | { type: "delete-table" };

export type TableRefusal =
  /** The selection is not in a table, or is where a table cannot go (inside another table). */
  | "invalid-target"
  /** The table would pass `maxTableRows` or `maxTableColumns`. */
  | "too-large";
